import React, { useMemo } from 'react';
import {
  autoCompleteSelection,
  findMatchingInventories,
  getAttributeKeys,
  getOptionMeta,
  getUniqueAttributeOptions,
  normalizeAttributeValue,
} from '../../utils/productVariants';

/**
 * Variant chips (Size, Color, etc.) — text only; images live in the gallery carousel.
 */
export default function ProductVariantPicker({
  attributes: productAttributes,
  inventories,
  selectedAttributes,
  onChange,
  compact = false,
}) {
  const attributeKeys = useMemo(
    () => getAttributeKeys(inventories, productAttributes),
    [inventories, productAttributes]
  );

  const optionMap = useMemo(
    () => getUniqueAttributeOptions(inventories),
    [inventories]
  );

  if (!inventories?.length || attributeKeys.length === 0) return null;

  const handleSelect = (attributeKey, value) => {
    const next = autoCompleteSelection(
      inventories,
      { ...selectedAttributes, [attributeKey]: value },
      attributeKeys
    );
    onChange(next);
  };

  const exactMatch = findMatchingInventories(inventories, selectedAttributes);
  const resolvedInventory = exactMatch.length === 1 ? exactMatch[0] : null;

  return (
    <div className="space-y-2">
      <div
        className={`rounded-lg border border-slate-200 bg-white overflow-hidden ${
          compact ? '' : 'shadow-sm'
        }`}
        role="group"
        aria-label="Product options"
      >
        {attributeKeys.map((attributeKey, rowIndex) => {
          const options = optionMap[attributeKey] || [];

          return (
            <div
              key={attributeKey}
              className={`px-2.5 py-2 sm:px-3 ${
                rowIndex > 0 ? 'border-t border-slate-100' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold text-slate-700 capitalize">
                  {attributeKey}
                </span>
                {selectedAttributes[attributeKey] && (
                  <span className="text-[10px] text-brand-700 font-medium truncate max-w-[55%]">
                    {normalizeAttributeValue(selectedAttributes[attributeKey])}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={attributeKey}>
                {options.map((option) => {
                  const value = option.value;
                  const isSelected =
                    normalizeAttributeValue(selectedAttributes[attributeKey]) === value;
                  const meta = getOptionMeta(
                    inventories,
                    attributeKey,
                    value,
                    selectedAttributes
                  );
                  const isDisabled = !meta.isAvailable;

                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      disabled={isDisabled}
                      title={
                        isDisabled
                          ? 'Unavailable'
                          : meta.totalStock
                            ? `${meta.totalStock} in stock`
                            : value
                      }
                      onClick={() => !isDisabled && handleSelect(attributeKey, value)}
                      className={`min-h-[30px] px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-300 ease-out
                        ${
                          isSelected
                            ? 'bg-brand-600 text-white border-brand-600 ring-2 ring-brand-600/25 scale-[1.02]'
                            : 'bg-white text-slate-800 border-slate-200 hover:border-brand-500 hover:scale-[1.01]'
                        }
                        ${isDisabled ? 'opacity-35 cursor-not-allowed line-through' : ''}`}
                    >
                      {value}
                      {!isDisabled && meta.totalStock > 0 && meta.totalStock < 20 && (
                        <span className="ml-1 text-[9px] opacity-75">({meta.totalStock})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {resolvedInventory && (
        <p className="text-[10px] text-slate-500 px-0.5">
          {Object.values(selectedAttributes)
            .filter(Boolean)
            .map(normalizeAttributeValue)
            .join(' · ')}
          {resolvedInventory.sku && (
            <span className="text-slate-400"> · SKU {resolvedInventory.sku}</span>
          )}
        </p>
      )}
    </div>
  );
}
